import type { Term, Tone } from "@/lib/campaign";
import type { Firm } from "@/lib/types";

/**
 * Firms (ADR-045 in `platform`), in words: the four statuses and what a change of one does, the
 * form's values and the arithmetic to `POST /firms` and `PUT /firms/{id}`, and the tag rules the
 * resolver applies (`normalize_tag`). Pure, for `test/firms.test.ts`.
 */

export const FIRM_STATUSES = ["active", "not_started", "paused", "done"] as const;
export type FirmStatus = (typeof FIRM_STATUSES)[number];

const STATUS: Record<FirmStatus, Term & { tone: Tone }> = {
  active: { label: "Active", description: "Its leads are worked on the calling campaign.", tone: "success" },
  not_started: {
    label: "Not started",
    description: "Signed up, not yet live: its leads are held, and new ones are refused at enlist.",
    tone: "outline",
  },
  paused: {
    label: "Paused",
    description: "Every list row of its leads is held, with the reason on each; new leads are refused.",
    tone: "warning",
  },
  done: { label: "Done", description: "The engagement ended. Its leads are held for good.", tone: "neutral" },
};

export function firmStatusText(status: string): Term & { tone: Tone } {
  return STATUS[status as FirmStatus] ?? { label: status, description: "Not a status this console knows.", tone: "outline" };
}

/** Whether a status stops the firm's dialing (`INACTIVE_STATUSES` in `models/firms.py`). */
export function stopsDialing(status: string): boolean {
  return status !== "active";
}

/** A tag as the resolver keeps it: lowercase, trimmed, spaces and dashes as underscores. */
export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().replaceAll(/[\s-]+/g, "_");
}

export const TAG_RE = /^[a-z0-9][a-z0-9_]{0,79}$/;

/** "ca, tx" -> ["CA", "TX"]; a problem for anything that is not two letters. */
export function parseStates(text: string): { states: string[]; problem: string | null } {
  const states = [...new Set(text.split(/[\s,]+/).map((s) => s.trim().toUpperCase()).filter(Boolean))];
  const bad = states.find((s) => !/^[A-Z]{2}$/.test(s));
  return { states, problem: bad ? `${bad} is not a two-letter state` : null };
}

/** "[CA] Larry H. Parker": the label the sheet's convention gives, offered as a default. */
export function suggestedLabel(name: string, states: readonly string[]): string {
  const prefix = states.length === 0 ? "" : states.length > 2 ? "[Multi] " : `[${states.join(", ")}] `;
  return `${prefix}${name.trim()}`;
}

export const SYSTEMS_OF_RECORD: Record<string, Term> = {
  ghl: { label: "GoHighLevel", description: "Until this firm cuts over: the nightly import overwrites stage and intake." },
  platform: { label: "The platform", description: "This is the firm's system of record; nothing overwrites it." },
};

export interface FirmValues {
  code: string;
  name: string;
  label: string;
  states: string;
  languages: string[];
  status: string;
  pause_reason: string;
  system_of_record: string;
}

export const NEW_FIRM: FirmValues = {
  code: "",
  name: "",
  label: "",
  states: "",
  languages: ["en"],
  status: "active",
  pause_reason: "",
  system_of_record: "ghl",
};

export function firmValues(firm: Firm): FirmValues {
  const record = firm.settings.system_of_record;
  return {
    code: firm.code,
    name: firm.name,
    label: firm.label,
    states: firm.states.join(", "),
    languages: [...firm.languages],
    status: firm.status,
    pause_reason: firm.pause_reason ?? "",
    system_of_record: typeof record === "string" && record in SYSTEMS_OF_RECORD ? record : "ghl",
  };
}

export interface FirmInput {
  code?: string;
  name?: string;
  label?: string;
  states?: string[];
  languages?: string[];
  status?: string;
  pause_reason?: string | null;
  settings?: Record<string, unknown>;
  tags?: string[];
  tenant_id?: number | null;
}

/** What `POST /firms` takes: the code normalised, the states parsed, the label defaulted. */
export function newFirmPayload(values: FirmValues): FirmInput {
  const { states } = parseStates(values.states);
  return {
    code: normalizeTag(values.code),
    name: values.name.trim(),
    label: values.label.trim() || suggestedLabel(values.name, states),
    states,
    languages: values.languages,
    status: values.status,
    pause_reason: values.pause_reason.trim() || null,
    settings: { system_of_record: values.system_of_record },
  };
}

const sameList = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/** The fields an edit changed, as `PUT /firms/{id}` takes them, and nothing else. */
export function firmChanges(values: FirmValues, firm: Firm): FirmInput {
  const before = firmValues(firm);
  const out: FirmInput = {};
  if (values.name.trim() !== firm.name) out.name = values.name.trim();
  if (values.label.trim() !== firm.label) out.label = values.label.trim();
  const { states } = parseStates(values.states);
  if (!sameList(states, firm.states)) out.states = states;
  if (!sameList(values.languages, firm.languages)) out.languages = values.languages;
  if (values.status !== firm.status) out.status = values.status;
  if (values.pause_reason.trim() !== before.pause_reason) out.pause_reason = values.pause_reason.trim() || null;
  if (values.system_of_record !== before.system_of_record) {
    out.settings = { ...firm.settings, system_of_record: values.system_of_record };
  }
  return out;
}

/** The sentence above Save when the status change holds or releases the firm's leads. */
export function statusWarning(from: string, to: string, contacts: number): string | null {
  if (from === to) return null;
  const n = contacts.toLocaleString();
  if (stopsDialing(to) && !stopsDialing(from)) {
    return `Saving holds every list row of this firm's ${n} contacts in the same commit, with the reason on each, and refuses new leads for it at enlist. Nothing retires while it waits.`;
  }
  if (!stopsDialing(to) && stopsDialing(from)) {
    return `Saving releases exactly the holds this firm's pause put on its ${n} contacts. A hold a live booking gave stays.`;
  }
  return `Its ${n} contacts stay held; only the word changes.`;
}
